import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Edit2, Trash2, Save, X, Eye, EyeOff, Power, Filter } from "lucide-react";
import { ServicesImportExport } from "@/components/ServicesImportExport";

const PRICE_TYPES = [
  { key: "FIXED", label: "Fijo" },
  { key: "RANGE", label: "Rango" },
  { key: "FROM", label: "Desde" },
  { key: "VARIABLE", label: "Variable" },
  { key: "CONSULT", label: "Consultar" },
];

const emptyForm = {
  name: "", category_id: "", subcategory_id: "", brand_id: "", model_id: "",
  price_type: "FIXED", price: "", price_min: "", price_max: "", price_from: "",
  cost: "", parts_policy: "",
  estimated_time_minutes: "", active: true, visible_to_client: true, notes: "",
};

export default function AdminServices() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("");
  const [fSub, setFSub] = useState("");
  const [fBrand, setFBrand] = useState("");
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (fCat) params.set("category_id", fCat);
      if (fSub) params.set("subcategory_id", fSub);
      if (fBrand) params.set("brand_id", fBrand);
      const r = await api.get(`/services?${params.toString()}`);
      setRows(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    Promise.all([
      api.get("/categories").then((r) => setCats(r.data)),
      api.get("/brands").then((r) => setBrands(r.data)),
    ]);
  }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q, fCat, fSub, fBrand]);
  useEffect(() => {
    if (!fCat) { setSubs([]); setFSub(""); return; }
    api.get(`/subcategories?category_id=${fCat}`).then((r) => setSubs(r.data));
  }, [fCat]);

  // For modal: sub/model options
  const [modalSubs, setModalSubs] = useState([]);
  const [modalModels, setModalModels] = useState([]);
  useEffect(() => {
    if (!modal?.category_id) { setModalSubs([]); return; }
    api.get(`/subcategories?category_id=${modal.category_id}`).then((r) => setModalSubs(r.data));
  }, [modal?.category_id]);
  useEffect(() => {
    if (!modal?.subcategory_id) { setModalModels([]); return; }
    const p = new URLSearchParams({ subcategory_id: modal.subcategory_id });
    if (modal.brand_id) p.set("brand_id", modal.brand_id);
    api.get(`/models?${p.toString()}`).then((r) => setModalModels(r.data));
  }, [modal?.subcategory_id, modal?.brand_id]);

  function openNew() { setModal({ ...emptyForm }); }
  function openEdit(row) {
    setModal({
      ...emptyForm, ...row,
      price: row.price ?? "", price_min: row.price_min ?? "",
      price_max: row.price_max ?? "", price_from: row.price_from ?? "",
      cost: row.cost ?? "", parts_policy: row.parts_policy || "",
      estimated_time_minutes: row.estimated_time_minutes ?? "",
    });
  }

  async function save() {
    const p = { ...modal };
    for (const k of ["price", "price_min", "price_max", "price_from", "cost"]) {
      p[k] = p[k] === "" ? null : Number(p[k]);
    }
    p.estimated_time_minutes = p.estimated_time_minutes === "" ? null : Number(p.estimated_time_minutes);
    for (const k of ["category_id", "subcategory_id", "brand_id", "model_id", "parts_policy"]) {
      if (!p[k]) p[k] = null;
    }
    try {
      if (p.id) {
        const { id, ...rest } = p;
        await api.patch(`/services/${id}`, rest);
        toast.success("Servicio actualizado");
      } else {
        await api.post("/services", p);
        toast.success("Servicio creado");
      }
      setModal(null); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function toggleActive(row) {
    try { await api.patch(`/services/${row.id}`, { active: !row.active }); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function toggleVisible(row) {
    try { await api.patch(`/services/${row.id}`, { visible_to_client: !row.visible_to_client }); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function del(row) {
    if (!confirm(`¿Eliminar "${row.name}"?\n\nSi ya se usó en órdenes, se archiva (inactivo) para preservar el historial.`)) return;
    try {
      const r = await api.delete(`/services/${row.id}`);
      toast.success(r.data?.deactivated ? "Servicio archivado (tiene órdenes asociadas)" : "Servicio eliminado");
      refresh();
    }
    catch (e) { toast.error(formatApiError(e)); }
  }

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";

  function priceLabel(s) {
    if (s.price_type === "FIXED") return `$${s.price ?? "?"}`;
    if (s.price_type === "RANGE") return `$${s.price_min ?? "?"}–$${s.price_max ?? "?"}`;
    if (s.price_type === "FROM") return `desde $${s.price_from ?? "?"}`;
    if (s.price_type === "VARIABLE") return "Variable";
    if (s.price_type === "CONSULT") return "Consultar";
    return "—";
  }

  // Precio numérico "canónico" para calcular ganancia (usa price / from / min según tipo).
  // Null si no hay valor puntual (Variable / Consultar / rango vacío).
  function baselinePrice(s) {
    if (s.price_type === "FIXED") return typeof s.price === "number" ? s.price : null;
    if (s.price_type === "FROM") return typeof s.price_from === "number" ? s.price_from : null;
    if (s.price_type === "RANGE") return typeof s.price_min === "number" ? s.price_min : null;
    return null;
  }
  function costLabel(s) {
    if (s.cost == null) return null;
    return `$${Number(s.cost).toFixed(2)}`;
  }
  function profitFor(s) {
    const p = baselinePrice(s);
    if (p == null || s.cost == null) return null;
    return p - Number(s.cost);
  }

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Servicios y Precios</h1>
          <p className="text-slate-500 text-sm">Catálogo administrable del taller.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ServicesImportExport onApplied={refresh} />
          <button onClick={openNew} className="btn-primary" data-testid="services-new-button">
            <Plus size={16} /> Nuevo servicio
          </button>
        </div>
      </div>

      <div className="card-base p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar servicio…" className="input-base !pl-[42px]" data-testid="services-search" />
          </div>
          <select value={fCat} onChange={(e) => setFCat(e.target.value)} className="input-base w-auto" data-testid="services-filter-category">
            <option value="">Todas las categorías</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={fSub} onChange={(e) => setFSub(e.target.value)} className="input-base w-auto" disabled={!fCat}>
            <option value="">Todas las subcategorías</option>
            {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={fBrand} onChange={(e) => setFBrand(e.target.value)} className="input-base w-auto">
            <option value="">Todas las marcas</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div className="text-xs text-slate-500 font-mono">{loading ? "Cargando…" : `${rows.length} servicios`}</div>
      </div>

      <div className="card-base overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Servicio</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Categoría</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Marca</th>
                <th className="text-right px-3 py-2">Precio</th>
                <th className="text-right px-3 py-2">Costo</th>
                <th className="text-right px-3 py-2 hidden sm:table-cell">Ganancia</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Estado</th>
                <th className="text-right px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const profit = profitFor(r);
                const cLabel = costLabel(r);
                return (
                <tr key={r.id} className={`border-t border-slate-100 ${!r.active ? "opacity-50" : ""}`} data-testid={`service-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.name}
                    {r.notes && <div className="text-[10px] text-slate-500">{r.notes}</div>}
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell text-slate-600">{catName(r.category_id)}</td>
                  <td className="px-3 py-2 hidden lg:table-cell text-slate-600">{brandName(r.brand_id)}</td>
                  <td className="px-3 py-2 font-mono text-right">{priceLabel(r)}</td>
                  <td className="px-3 py-2 font-mono text-right" data-testid={`service-cost-${r.id}`}>
                    {cLabel ? (
                      <span className="text-slate-900">{cLabel}</span>
                    ) : (
                      <span className="text-amber-600 text-[11px] font-semibold">Sin costo</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-right hidden sm:table-cell" data-testid={`service-profit-${r.id}`}>
                    {profit == null ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <span className={profit >= 0 ? "text-emerald-700 font-semibold" : "text-red-600"}>${profit.toFixed(2)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    <span className={`chip ${r.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                      {r.active ? "Activo" : "Inactivo"}
                    </span>
                    {r.visible_to_client
                      ? <span className="ml-1 text-[10px] text-emerald-600" title="Visible al cliente"><Eye size={12} className="inline" /></span>
                      : <span className="ml-1 text-[10px] text-slate-400" title="Oculto al cliente"><EyeOff size={12} className="inline" /></span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => toggleVisible(r)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title={r.visible_to_client ? "Ocultar cliente" : "Mostrar cliente"}>
                      {r.visible_to_client ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button onClick={() => toggleActive(r)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Activar/Desactivar">
                      <Power size={14} />
                    </button>
                    <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-700" data-testid={`service-edit-${r.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => del(r)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600" data-testid={`service-delete-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">Sin resultados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && createPortal(
        <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setModal(null)}>
          <div className="relative bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="font-heading font-bold text-lg">{modal.id ? "Editar servicio" : "Nuevo servicio"}</h3>
              <button onClick={() => setModal(null)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="service-modal-close"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <input value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} placeholder="Nombre del servicio *" className="input-base" data-testid="service-modal-name" />
              <div className="grid grid-cols-2 gap-2">
                <select value={modal.category_id || ""} onChange={(e) => setModal({ ...modal, category_id: e.target.value, subcategory_id: "", brand_id: "", model_id: "" })} className="input-base">
                  <option value="">— Categoría —</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={modal.subcategory_id || ""} onChange={(e) => setModal({ ...modal, subcategory_id: e.target.value, model_id: "" })} className="input-base" disabled={!modal.category_id}>
                  <option value="">— Subcategoría —</option>
                  {modalSubs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select value={modal.brand_id || ""} onChange={(e) => setModal({ ...modal, brand_id: e.target.value, model_id: "" })} className="input-base">
                  <option value="">— Marca —</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select value={modal.model_id || ""} onChange={(e) => setModal({ ...modal, model_id: e.target.value })} className="input-base" disabled={!modal.subcategory_id}>
                  <option value="">— Modelo —</option>
                  {modalModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase text-slate-500 mb-1 block">Tipo de precio</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-1">
                  {PRICE_TYPES.map((pt) => (
                    <button key={pt.key} type="button" onClick={() => setModal({ ...modal, price_type: pt.key })}
                      className={`py-1.5 text-xs rounded-lg border ${modal.price_type === pt.key ? "bg-blue-600 text-white border-blue-600" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {pt.label}
                    </button>
                  ))}
                </div>
              </div>
              {modal.price_type === "FIXED" && (
                <input type="number" step="0.01" value={modal.price} onChange={(e) => setModal({ ...modal, price: e.target.value })} placeholder="Precio USD" className="input-base font-mono" data-testid="service-modal-price" />
              )}
              {modal.price_type === "RANGE" && (
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.01" value={modal.price_min} onChange={(e) => setModal({ ...modal, price_min: e.target.value })} placeholder="Mínimo" className="input-base font-mono" />
                  <input type="number" step="0.01" value={modal.price_max} onChange={(e) => setModal({ ...modal, price_max: e.target.value })} placeholder="Máximo" className="input-base font-mono" />
                </div>
              )}
              {modal.price_type === "FROM" && (
                <input type="number" step="0.01" value={modal.price_from} onChange={(e) => setModal({ ...modal, price_from: e.target.value })} placeholder="Desde" className="input-base font-mono" />
              )}

              {/* COSTO INTERNO — Admin only */}
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50/50 p-3">
                <label className="text-[10px] uppercase tracking-widest font-mono text-amber-700 block mb-1">
                  Costo interno (confidencial)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={modal.cost ?? ""}
                  onChange={(e) => setModal({ ...modal, cost: e.target.value })}
                  placeholder="Ej: 35.00 (dejar vacío = sin costo)"
                  className="input-base font-mono"
                  data-testid="service-modal-cost"
                />
                <select value={modal.parts_policy || ""} onChange={(e) => setModal({ ...modal, parts_policy: e.target.value })}
                  className="input-base mt-2 text-sm" data-testid="service-modal-parts-policy">
                  <option value="">Clasificación financiera — sin clasificar</option>
                  <option value="NO_PARTS">Sin repuesto (35% operativo imputado)</option>
                  <option value="WITH_PARTS">Con repuesto (costo real)</option>
                  <option value="PENDING">Pendiente</option>
                </select>
                {(() => {
                  const priceRef = modal.price_type === "FIXED" ? modal.price
                    : modal.price_type === "FROM" ? modal.price_from
                    : modal.price_type === "RANGE" ? modal.price_min : null;
                  const p = priceRef === "" || priceRef == null ? null : Number(priceRef);
                  const c = modal.cost === "" || modal.cost == null ? null : Number(modal.cost);
                  if (p == null || c == null || Number.isNaN(p) || Number.isNaN(c)) {
                    return (
                      <div className="text-[11px] text-slate-500 mt-1.5">
                        Ganancia estimada: <span className="text-slate-400">—</span>
                      </div>
                    );
                  }
                  const g = p - c;
                  return (
                    <div className="text-[11px] mt-1.5" data-testid="service-modal-profit-preview">
                      Ganancia estimada:{" "}
                      <b className={g >= 0 ? "text-emerald-700 font-mono" : "text-red-600 font-mono"}>
                        ${g.toFixed(2)}
                      </b>
                    </div>
                  );
                })()}
              </div>

              <input type="number" value={modal.estimated_time_minutes} onChange={(e) => setModal({ ...modal, estimated_time_minutes: e.target.value })} placeholder="Tiempo estimado (minutos)" className="input-base font-mono" />
              <textarea value={modal.notes} onChange={(e) => setModal({ ...modal, notes: e.target.value })} placeholder="Notas internas u observación del catálogo…" className="input-base min-h-[60px]" />
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={modal.active} onChange={(e) => setModal({ ...modal, active: e.target.checked })} className="w-4 h-4 accent-blue-600" />
                  Activo
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={modal.visible_to_client} onChange={(e) => setModal({ ...modal, visible_to_client: e.target.checked })} className="w-4 h-4 accent-blue-600" />
                  Visible al cliente
                </label>
              </div>
            </div>
            <div className="border-t border-slate-200 p-3 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="btn-secondary">Cancelar</button>
              <button onClick={save} className="btn-primary" data-testid="service-modal-save"><Save size={16} /> Guardar</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
